// ** React Imports
import React, { Fragment, useState, useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

// ** MUI Imports
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Stepper from '@mui/material/Stepper'
import MenuItem from '@mui/material/MenuItem'
import Checkbox from '@mui/material/Checkbox'
import ListItemText from '@mui/material/ListItemText'
import StepLabel from '@mui/material/StepLabel'
import Typography from '@mui/material/Typography'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import IconButton from '@mui/material/IconButton'
import CardContent from '@mui/material/CardContent'
import { styled } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import MuiStep from '@mui/material/Step'
import InputAdornment from '@mui/material/InputAdornment'
import IgnoreIcon from '@mui/icons-material/VisibilityOff'
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CardHeader from '@mui/material/CardHeader'
import { DataGrid } from '@mui/x-data-grid'
import { DataGridPro } from '@mui/x-data-grid-pro';
import {TextField, Select, FormControl, InputLabel } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import ListSubheader from '@mui/material/ListSubheader'
import { Dialog,
  DialogTitle,
  DialogContent,
  DialogActions, } from '@mui/material';
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import { ToggleButtonGroup, ToggleButton } from '@mui/material';

// Define the styles for the invalid email cell

// ** Third Party Imports
import axios from 'axios'

// ** Icon Imports
import Icon from '../../../../../components/icon'
import XLSX_Placeholder from "images/Attachment_Icons/XLSX__PlaceholderIcon.png";
import CSV_Placeholder from "images/Attachment_Icons/CSV__PlaceholderIcon.png";

// ** Custom Components Imports
import StepperCustomDot from '../../../../../components/mui/StepperCustomDot'
import CustomTextField from '../../../../../components/mui/text-field'
import CustomChip from '../../../../../components/mui/chip'
import CustomAvatar from '../../../../../components/mui/avatar'
import ServerSideToolbar from '../../../../../components/data-grid/ServerSideToolbar'
// import ServerSideToolbar_WithoutSearch from '../../../../../components/data-grid/ServerSideToolbar_WithoutSearch'

// ** Hook Import
import { useSettings } from '../../../../../components/hooks/useSettings'

// ** Util Import
import { getInitials } from '../../../../../components/utils/get-initials'

// ** Styled Component
import StepperWrapper from '../../../../../components/styles/mui/stepper'

// ** Layout Import
import BlankLayout from '../../../../Layout/blankLayout'
import { GetTypeCatalog, SaveTypeCatalog } from 'screens/Inventory/logic/TypeCatalogService'
import { SaveAsset } from 'screens/Inventory/logic/AssetService'

const steps = [
  {
    title: 'Map Columns'
  },
  {
    title: 'Fix Errors'
  }
]

const Step = styled(MuiStep)(({ theme }) => ({
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  '&:first-of-type': {
    paddingLeft: 0
  },
  '&:last-of-type': {
    paddingRight: 0
  },
  '& .MuiStepLabel-iconContainer': {
    display: 'none'
  },
  '& .step-subtitle': {
    color: `${theme.palette.text.disabled} !important`
  },
  '& + svg': {
    color: theme.palette.text.disabled
  },
  '&.Mui-completed .step-title': {
    color: theme.palette.text.disabled
  },
  '&.Mui-completed + svg': {
    color: theme.palette.primary.main
  },
  [theme.breakpoints.down('md')]: {
    padding: 0,
    ':not(:last-of-type)': {
      marginBottom: theme.spacing(6)
    }
  }
}))

// ** renders client column
const renderClient = params => {
    const { row } = params
    const stateNum = Math.floor(Math.random() * 6)
    const states = ['success', 'error', 'warning', 'info', 'primary', 'secondary']
    const color = states[stateNum]
  }
  
  const statusObj = {
    1: { title: 'current', color: 'primary' },
    2: { title: 'professional', color: 'success' },
    3: { title: 'rejected', color: 'error' },
    4: { title: 'resigned', color: 'warning' },
    5: { title: 'applied', color: 'info' }
  }
const StepAssetUploadFile = () => {
  
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('jwtToken');
    if (!token) {
      navigate('/login', { state: { message: 'Please login to continue.' } }); // Redirect to login if token is not found
    } else {
      setIsLoading(false); // Set loading to false if token is found
    }
  }, [navigate]);

  const location = useLocation();
  const fileData = location.state?.data || []

  const columnNamesFromFile = fileData.length > 0 
  ? Object.keys(fileData[0]).filter(columnName => {

      return fileData.some(row => row[columnName] && row[columnName].toString().trim() !== '');
    }) 
  : [];
    

  const predefinedFields = ['Asset Name'];
  const additionalFields = [
    'Asset Type','Asset Description','Serial/VIN Number','License Plate','Hourly Rate','Operational Cost','Operational Cost Unit','Designated Operator','Status','Next Maintenance Date','Last Maintenance Date',
    'Purchase Date','Purchase Price','Warranty Expiration Date'
  ];

  const fieldsToRender = [...predefinedFields, ...additionalFields];

  const getDefaultValues = () => {
    const initialValues = {};
    columnNamesFromFile.forEach((columnName) => {
      initialValues[columnName] = fieldsToRender.includes(columnName)
        ? columnName
        : fieldsToRender.some(field => field.replace(/\s/g, '').toLowerCase() === columnName.replace(/\s/g, '').toLowerCase())
          ? fieldsToRender.find(field => field.replace(/\s/g, '').toLowerCase() === columnName.replace(/\s/g, '').toLowerCase())
          : 'select';
    });
    return initialValues;
  };

  const [isMappingConfirmed, setIsMappingConfirmed] = useState(false);
  const [isIgnored, setIsIgnored] = useState(false);

  // ** States
  const [activeStep, setActiveStep] = useState(0)
  const [total, setTotal] = useState(0)
  const [sort, setSort] = useState('asc')
  const [searchValue, setSearchValue] = useState('')
  const [sortColumn, setSortColumn] = useState('first_name')
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 7 })
  const [mappingState, setMappingState] = useState({});
  const [selectedValues, setSelectedValues] = useState(getDefaultValues()); // Track selected dropdown values
  const [errorState, setErrorState] = useState({}); 
  const [myData, setMyData] = useState([]); 
  const [columns, setColumns] = useState([]); 
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(location.state?.message || '');
  const [errorMessages, setErrorMessages] = useState([]);
  const [assets, setAssets] = useState([]);
  const [openErrorListDlg, setOpenErrorListDlg] = useState(false);
  const [openSuccessListDlg, setOpenSuccessListDlg] = useState(false);
  const [successMessages, setSuccessMessages] = useState('');
  const [alertIsDismissed, setAlertIsDismissed] = useState(false);
  const [showConfirmAlert, setShowConfirmAlert] = useState(true);
   const [displayBanner, setDisplayBanner] = useState(true);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if(showConfirmAlert){
        e.preventDefault(); 
        e.returnValue = ''; 
      }        
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [showConfirmAlert]);

  useEffect(() => {
    const fetchAssets = async () => {
      const ast = await GetTypeCatalog('Asset');

      if (!ast.isError){
        let a = ast?.result || [];
        
        // Check if "Other" type exists, if not create it
        const hasOther = a.some(asset => asset.typeName === 'Other');
        if (!hasOther) {
          await SaveTypeCatalog({
            typeCatalogID: 0,
            entityType: 'Asset',
            typeName: 'Other',
            name: 'Other'
          });
          // Re-fetch after creating "Other"
          const refreshedAst = await GetTypeCatalog('Asset');
          if (!refreshedAst.isError) {
            a = refreshedAst.result || [];
          }
        }
        
        setAssets(a.map(asset => ({ key: asset.typeCatalogID, value: asset.typeName })));
      }
    };
  
    fetchAssets();
  }, [navigate]);


  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage('');
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  function loadServerRows(currentPage, data) {
    return data.slice(currentPage * paginationModel.pageSize, (currentPage + 1) * paginationModel.pageSize);
  }

  const fetchTableData = useCallback(
    async (sort, q, column) => {
      let filteredData = [...myData];
  
      if (q) {
        filteredData = filteredData.filter(row =>
          Object.values(row).some(value =>
            value.toString().toLowerCase().includes(q.toLowerCase())
          )
        );
      }
        
      if (sort && column) {
        filteredData = filteredData.sort((a, b) => {
          if (a[column] < b[column]) return sort === 'asc' ? -1 : 1;
          if (a[column] > b[column]) return sort === 'asc' ? 1 : -1;
          return 0;
        });
      }
  
      setTotal(filteredData.length);
      setRows(loadServerRows(paginationModel.page, filteredData));
    },
    [paginationModel.page, paginationModel.pageSize, myData, sort, searchValue, sortColumn]

  );

  useEffect(() => {
    fetchTableData(sort, searchValue, sortColumn)
  }, [fetchTableData, searchValue, sort, sortColumn])

  const handleAlertDismiss = () => {
    setAlertIsDismissed(true);
  }
  
  // Get list of unmapped mandatory fields
  const getUnmappedMandatoryFields = () => {
    const mandatoryFields = ['Asset Name'];
    return mandatoryFields.filter(field => {
      // Check if this field is mapped and confirmed
      const isMapped = Object.values(mappingState).some(
        mapping => mapping.mappedValue === field && mapping.confirmed
      );
      return !isMapped;
    });
  };
  
  const unmappedFields = getUnmappedMandatoryFields();
  const showMandatoryAlert = !alertIsDismissed && unmappedFields.length > 0;

  const handleSortModel = newModel => {
    if (newModel.length) {
      setSort(newModel[0].sort);
      setSortColumn(newModel[0].field);
      fetchTableData(newModel[0].sort, searchValue, newModel[0].field);
    } else {
      setSort('asc');
      setSortColumn(Object.keys(myData[0])[0]); // Sort by the first key in `mappedData`
    }
  };

  const handleSearch = value => {
    setSearchValue(value);
    fetchTableData(sort, value, sortColumn);
  };

  const statusOptions = ['Available', 'Unavailable']
  const operationalCostUnitOptions = ['Hour', 'Day', 'Week', 'Month', 'Year']

  // Helper to normalize status values (case-insensitive)
  const normalizeStatus = (status) => {
    if (!status) return null;
    const lowerStatus = status.toString().trim().toLowerCase();
    if (lowerStatus === 'available') return 'Available';
    if (lowerStatus === 'unavailable') return 'Unavailable';
    return null; // Invalid status
  };

  // const predefinedFields = ['First Name', 'Last Name', 'Email'];
  // const additionalFields = [
  //   'Phone', 'Role', 'Status', 'Date of Birth', 'Referred By', 'Address', 'City', 'State/Province',
  //   'Postal Code', 'Country', 'Emergency Contact Name', 'Emergency Contact Relationship',
  //   'Emergency Contact Phone Number', 'Hire Date', 'Employment Status', 'Departments',
  //   'Job Title', 'Probation End Date', 'Driving license','Driving license expiry', 'Employee Wages Amount',
  //   'Employee Wages Type', 'Availability', 'Clock in / out'
  // ];

  const errorColumns = [
    {
      flex: 0.25,
      minWidth: 180,
      field: 'assetName',      
      headerName: 'Asset Name'
    },
    {
      flex: 0.25,
      minWidth: 180,      
      headerName: 'Reason',
      field: 'failureReason'
    }       
  ]

  const handleCloseErrorListDlg = () => {
    // sessionStorage.removeItem('myStep');
    // sessionStorage.setItem('myStep', 3);
    setOpenErrorListDlg(false)
    navigate('/onboarding', { state: { step: 8, skipWelcome: true } });
    // navigate('/onboarding');
  }

  const handleCloseSuccessListDlg = () => {
    // sessionStorage.removeItem('myStep');
    // sessionStorage.setItem('myStep', 4);
    setOpenSuccessListDlg(false);
    navigate('/onboarding', { state: { step: 8, skipWelcome: true } });
    // handleNext();
    // navigate('/onboarding');
  }

  const excelDateToJSDate = (excelDate) => {
    const serialDate = Number(excelDate); 
    const excelStartDate = new Date(Date.UTC(1900, 0, 1)); 
    const jsDate = new Date(excelStartDate.getTime() + (serialDate - 1) * 24 * 60 * 60 * 1000); 
  
    if (serialDate > 59) {
      jsDate.setUTCDate(jsDate.getUTCDate() - 1);
    }
  
    return jsDate;
  };
  
  // const formatDate = (date) => {
  //   if (isNaN(date.getTime())) {
  //     return 'Invalid Date';
  //   }
  
  //   const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Get month in UTC
  //   const day = String(date.getUTCDate()).padStart(2, '0'); // Get day in UTC
  //   const year = date.getUTCFullYear(); // Get year in UTC
  
  //   return `${month}-${day}-${year}`; // Format as MM-DD-YYYY
  // };

  const parseDateAsUTC = (dateString) => {
    const [month, day, year] = dateString.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day)); // Creates a UTC date
  };
  
  const formatDate = (date) => {
    const jsDate = typeof date === 'string' ? parseDateAsUTC(date) : date;
    
    if (isNaN(jsDate.getTime())) {
      return 'Invalid Date';
    }
  
    const month = String(jsDate.getUTCMonth() + 1).padStart(2, '0');
    const day = String(jsDate.getUTCDate()).padStart(2, '0');
    const year = jsDate.getUTCFullYear();
  
    return `${month}-${day}-${year}`;
  };
  
  // ** Hooks & Var
  const { settings } = useSettings()
  const smallScreen = useMediaQuery(theme => theme.breakpoints.down('md'))
  const { direction } = settings
  
    const updateSelectedValues = () => {
      // Create the new selectedValues object based on mappings
      const newSelectedValues = columnNamesFromFile.reduce((acc, columnName) => {
        if (predefinedFields.includes(columnName)) {
          acc[columnName] = selectedValues[columnName] || columnName;
        }
        return acc;
      }, {});
  
      // Update the state with the new object
      setSelectedValues(newSelectedValues);
    };
  
    useEffect(() => {
      updateSelectedValues();
    }, []);
  
  
    // const handleSelectChange = (columnName) => (event) => {
    //   const newValue = event.target.value;
    
    //   setSelectedValues((prev) => {
  
    //     const updatedValues = { ...prev, [columnName]: newValue };
  
    //     // Re-check for conflicts across all columns
    //     const newErrorState = {};
    //     const valueCounts = {};
    
    //     // Count occurrences of each selected value
    //     Object.values(updatedValues).forEach((value) => {
    //       valueCounts[value] = (valueCounts[value] || 0) + 1;
    //     });
    
    //     // Identify conflicts and update the error state
    //     Object.keys(updatedValues).forEach((key) => {
    //       const value = updatedValues[key];
    //       if (valueCounts[value] > 1 ) {
    //         newErrorState[key] = `${value} has already been matched to another column`;
    //       } 
    //     });
  
    //     setErrorState(newErrorState);
    
    //     return updatedValues;
    //   });
    // };
    const handleSelectChange = (columnName) => (event) => {
      const newValue = event.target.value;
      
      setSelectedValues((prev) => {
        const updatedValues = { ...prev, [columnName]: newValue };
        
        // Re-check for conflicts across all columns
        const newErrorState = {};
        const valueCounts = {};
        
        // Count occurrences of each selected value (EXCLUDING 'select' and empty values)
        Object.values(updatedValues).forEach((value) => {
          if (value && value !== 'select' && value !== '') {
            valueCounts[value] = (valueCounts[value] || 0) + 1;
          }
        });
        
        // Identify conflicts and update the error state
        Object.keys(updatedValues).forEach((key) => {
          const value = updatedValues[key];
          // Only show error for non-select values that have duplicates
          if (value && value !== 'select' && value !== '' && valueCounts[value] > 1) {
            newErrorState[key] = `${value} has already been matched to another column`;
          }
        });
        
        setErrorState(newErrorState);
        
        return updatedValues;
      });
    };

    const handleConfirmClick = (columnName) => {
  
      const selectedValue = selectedValues[columnName] || (fieldsToRender.includes(columnName) 
      ? columnName 
      : fieldsToRender.some(field => field.replace(/\s/g, '').toLowerCase() === columnName.replace(/\s/g, '').toLowerCase()) 
        ? fieldsToRender.find(field => field.replace(/\s/g, '').toLowerCase() === columnName.replace(/\s/g, '').toLowerCase()) 
        : 'select');
    
      if (!selectedValue || selectedValue === 'select') {
        setMessage('Please select a valid field before confirming the mapping.');
        return;
      }
    
      setMappingState((prevState) => ({
        ...prevState,
        [columnName]: {
          ...prevState[columnName],
          confirmed: true,
          ignored: false,
          mappedValue: selectedValue || ''
        }
      }));
    };
    
    // const handleIgnoreClick = (columnName) => {
    //   setMappingState((prevState) => ({
    //     ...prevState,
    //     [columnName]: {
    //       confirmed: false,
    //       ignored: true,
    //       mappedValue: '' // Clear the mapped value
    //     }
    //   }));
    // };
    
    const handleEditClick = (columnName) => {
      setMappingState((prevState) => ({
        ...prevState,
        [columnName]: {
          confirmed: false,
          ignored: false,
          mappedValue: '' // Reset the mapping
        }
      }));
    }
  
    // const isConfirmDisabled = (columnName) => {
    //   const selectedValue = selectedValues[columnName] || mappingState[columnName]?.mappedValue || (fieldsToRender.includes(columnName) 
    //   ? columnName 
    //   : fieldsToRender.some(field => field.replace(/\s/g, '').toLowerCase() === columnName.replace(/\s/g, '').toLowerCase()) 
    //     ? fieldsToRender.find(field => field.replace(/\s/g, '').toLowerCase() === columnName.replace(/\s/g, '').toLowerCase()) 
    //     : 'select');
    //   const hasError = Boolean(
    //     selectedValues[columnName] && Object.values(selectedValues).filter(val => val === selectedValue).length > 1
    //   );
    
    //   // Disable if default value is 'select' or if there is an error for the field
    //   return selectedValue === 'select' || hasError;
    // };
    const isConfirmDisabled = (columnName) => {
      const selectedValue = selectedValues[columnName] || mappingState[columnName]?.mappedValue || (fieldsToRender.includes(columnName) 
      ? columnName 
      : fieldsToRender.some(field => field.replace(/\s/g, '').toLowerCase() === columnName.replace(/\s/g, '').toLowerCase()) 
        ? fieldsToRender.find(field => field.replace(/\s/g, '').toLowerCase() === columnName.replace(/\s/g, '').toLowerCase()) 
        : 'select');
      
      // Skip error checking for 'select' values
      if (selectedValue === 'select' || !selectedValue) {
        return true;
      }
      
      const hasError = Boolean(
        selectedValues[columnName] && 
        selectedValues[columnName] !== 'select' &&
        Object.values(selectedValues).filter(val => val === selectedValue && val !== 'select').length > 1
      );
      
      return hasError;
    };

    const handleIgnoreClick = (columnName) => {
      setMappingState((prevState) => ({
        ...prevState,
        [columnName]: {
          confirmed: false,
          ignored: true,
          mappedValue: '' // Clear the mapped value
        }
      }));
    
      // Set to 'select' and recalculate all errors
      setSelectedValues((prev) => {
        const updatedValues = { ...prev, [columnName]: 'select' };
        
        // Re-check for conflicts across all columns
        const newErrorState = {};
        const valueCounts = {};
        
        // Count occurrences of each selected value (excluding 'select' and empty)
        Object.values(updatedValues).forEach((value) => {
          if (value && value !== 'select' && value !== '') {
            valueCounts[value] = (valueCounts[value] || 0) + 1;
          }
        });
        
        // Identify conflicts and update the error state
        Object.keys(updatedValues).forEach((key) => {
          const value = updatedValues[key];
          // Only show error for non-select values that have duplicates
          if (value && value !== 'select' && value !== '' && valueCounts[value] > 1) {
            newErrorState[key] = `${value} has already been matched to another column`;
          }
        });
        
        setErrorState(newErrorState);
        
        return updatedValues;
      });
    };

  const handleBack = () => {
    if (!window.confirm("Are you sure you want to go back? Your changes might not be saved.")) return;
    if (activeStep === 0) {
      // BACK → Connect to Stripe (step 2)
      navigate('/onboarding', { state: { step: 6, skipWelcome: true } });
    } else {
      // internal back: Map Columns ↔ Fix Errors
      setActiveStep(s => s - 1);
    }
  };

  const assetOptions = assets;

  //const columns = Object.keys(mappedData[0] || {}).map(key => {
const buildColumns = () =>
  Object.keys(myData[0] || {}).filter(key => key !== 'id').map(key => {
 
 if (key === 'Asset Type') {
   return {
     field: key,
     headerName: key,
     flex: 1,
     minWidth: 100,
     editable: true,
     renderEditCell: params => {
       const id   = params.id
       const val  = params.value  // this should be the numeric key
       return (
         <Select
           value={val ?? ''}
           onChange={e => {
             const newKey = e.target.value
             // update DataGrid's internal value:
             params.api.setEditCellValue({ id, field: key, value: newKey }, e)
             // update your state:
             setMyData(prev =>
                 prev.map(r =>
                   r.id === params.id
                     ? { ...r, ['Asset Type']: newKey }   // ← correctly update the 'Asset Type' field
                     : r
                 )
               )
           }}
         >
           {assetOptions.map(opt => (
             <MenuItem key={opt.key} value={opt.key}>
               {opt.value}
             </MenuItem>
           ))}
           <MenuItem value={''}>None</MenuItem>
         </Select>
       )
     },      
    renderCell: (params) => {
     // params.value is the numeric key
     const asset = assetOptions.find(a => a.key === params.value)
     const display = asset ? asset.value : 'None'
     // Asset Type is now optional, so "None" is valid (will default to "Other" on save)
     const isValidAsset = true; // Always valid since it's optional
      
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            height: '100%',
            width: '100%'
          }}
        >
          <Box sx={{
            backgroundColor: 'transparent',
            padding: '0px',
            borderRadius: '4px',
            width: '100%'
          }}>             
              <Typography variant="body2">{display}</Typography>             
          </Box>
        </Box>
      );
    },
   };
 } else if (key === 'Status') {
   return {
     field: key,
     headerName: key,
     flex: 1,
     minWidth: 100,
     editable: true,
     renderEditCell: (params) => (
       <Select
         value={params.value || ''} // Ensure value is a string
         onChange={(event) => {
           const value = event.target.value;
           // Use params.api.setEditCellValue to update the cell value
           params.api.setEditCellValue({ id: params.id, field: key, value }, event);

           setMyData((prevData) =>
             prevData.map((row) =>
               row.id === params.id ? { ...row, [key]: value } : row
             )
           );

           // Update rows state directly here
           setRows((prevRows) =>
             prevRows.map((row) =>
               row.id === params.id ? { ...row, [key]: value } : row
             )
           );
         }}
         renderValue={(selected) => selected || 'None'} // Show 'None' if no selection
       >
         {statusOptions.map(status => (
           <MenuItem key={status} value={status}>
             <ListItemText primary={status} />
           </MenuItem>
         ))}
         <MenuItem key="none" value="">
           <ListItemText primary="None" />
         </MenuItem>
       </Select>
     ),
     renderCell: (params) => {
       const normalizedStatus = normalizeStatus(params.value);
       const isValidStatus = normalizedStatus !== null;
       const displayStatusValue = isValidStatus ? normalizedStatus : 'None';
       return (
         <Box
           sx={{
             display: 'flex',
             alignItems: 'center',
             position: 'relative',
             height: '100%',
             width: '100%'
           }}
         >
          <Tooltip
            title={isValidStatus ? '' : `${params.value} is not a valid value. Please select valid value from dropdown`}
            placement="bottom"
            arrow
          >
           <Box sx={{
             backgroundColor: isValidStatus ? 'transparent' : 'rgba(227, 0, 0, 0.1)',
             padding: isValidStatus ? '0px' : '8px',
             borderRadius: '4px',
             width: '100%'
           }}>             
               <Typography variant="body2">
                 {displayStatusValue}
               </Typography>
           </Box>
          </Tooltip>
         </Box>
       );
     },
   };
 } else if (key === 'Operational Cost Unit') {
   return {
     field: key,
     headerName: key,
     flex: 1,
     minWidth: 150,
     editable: true,
     renderEditCell: (params) => (
       <Select
         value={params.value || 'Day'} // Default to 'Day'
         onChange={(event) => {
           const value = event.target.value;
           params.api.setEditCellValue({ id: params.id, field: key, value }, event);

           setMyData((prevData) =>
             prevData.map((row) =>
               row.id === params.id ? { ...row, [key]: value } : row
             )
           );

           setRows((prevRows) =>
             prevRows.map((row) =>
               row.id === params.id ? { ...row, [key]: value } : row
             )
           );
         }}
         renderValue={(selected) => selected || 'Day'}
       >
         {operationalCostUnitOptions.map(unit => (
           <MenuItem key={unit} value={unit}>
             <ListItemText primary={unit} />
           </MenuItem>
         ))}
       </Select>
     ),
     renderCell: (params) => {
       const isValidUnit = operationalCostUnitOptions.includes(params.value);
       const displayValue = isValidUnit ? params.value : 'Day'; // Default to 'Day' if invalid
       return (
         <Box
           sx={{
             display: 'flex',
             alignItems: 'center',
             position: 'relative',
             height: '100%',
             width: '100%'
           }}
         >
           <Box sx={{
             backgroundColor: 'transparent',
             padding: '0px',
             borderRadius: '4px',
             width: '100%'
           }}>             
               <Typography variant="body2">
                 {displayValue}
               </Typography>
           </Box>
         </Box>
       );
     },
   };
 } else if (key === 'Next Maintenance Date') {
   return {
     field: key,
     headerName: key,
     flex: 1,
     minWidth: 100,
     editable: true,
     renderEditCell: (params) => (
       <TextField
         value={params.value || ''}
         onChange={(event) => {
           const value = event.target.value;

           // Update the grid's internal state
           params.api.setEditCellValue({ id: params.id, field: key, value }, event);

           setMyData((prevData) =>
             prevData.map((row) =>
               row.id === params.id ? { ...row, [key]: value } : row
             )
           );

           // Update component's rows state
           setRows((prevRows) =>
             prevRows.map((row) =>
               row.id === params.id ? { ...row, [key]: value } : row
             )
           );
         }}
         placeholder="MM-DD-YYYY"
         variant="outlined"
         error={!/^\d{2}-\d{2}-\d{4}$/.test(params.value)}
         helperText={!/^\d{2}-\d{2}-\d{4}$/.test(params.value) ? 'Invalid date format' : ''}
       />
     ),
     renderCell: (params) => {
       const excelDate = params.value;
       const isNumber = !isNaN(excelDate);
       const jsDate = isNumber ? excelDateToJSDate(excelDate) : parseDateAsUTC(excelDate); // Convert if it's a number
       const displayDate = formatDate(jsDate); // Format to MM-DD-YYYY
     
       // Date validation (MM-DD-YYYY format)
       const isValidDate = /^\d{2}-\d{2}-\d{4}$/.test(displayDate);
     
       return (
         <Box
           sx={{
             display: 'flex',
             alignItems: 'center',
             position: 'relative',
             height: '100%',
             width: '100%'
           }}
         >
          <Tooltip
            title={
              isValidDate
                ? ''
                : 'Invalid date format. Please use format MM-DD-YYYY.'
            }
            placement="bottom"
            arrow
          >
           <Box sx={{
             backgroundColor: isValidDate ? 'transparent' : 'rgba(227, 0, 0, 0.1)',
             padding: isValidDate ? '0px' : '8px',
             borderRadius: '4px',
             width: '100%'
           }}>
             
               <Typography variant="body2">{displayDate}</Typography>
           </Box>
          </Tooltip>
         </Box>
       );
     },      
   };
 } else if (key === 'Last Maintenance Date') {
   return {
     field: key,
     headerName: key,
     flex: 1,
     minWidth: 100,
     editable: true,
     renderEditCell: (params) => (
       <TextField
         value={params.value || ''}
         onChange={(event) => {
           const value = event.target.value;

           // Update the grid's internal state
           params.api.setEditCellValue({ id: params.id, field: key, value }, event);

           setMyData((prevData) =>
             prevData.map((row) =>
               row.id === params.id ? { ...row, [key]: value } : row
             )
           );

           // Update component's rows state
           setRows((prevRows) =>
             prevRows.map((row) =>
               row.id === params.id ? { ...row, [key]: value } : row
             )
           );
         }}
         placeholder="MM-DD-YYYY"
         variant="outlined"
         error={!/^\d{2}-\d{2}-\d{4}$/.test(params.value)}
         helperText={!/^\d{2}-\d{2}-\d{4}$/.test(params.value) ? 'Invalid date format' : ''}
       />
     ),
     renderCell: (params) => {
       const excelDate = params.value;
       const isNumber = !isNaN(excelDate);
       const jsDate = isNumber ? excelDateToJSDate(excelDate) : parseDateAsUTC(excelDate); // Convert if it's a number
       const displayDate = formatDate(jsDate); // Format to MM-DD-YYYY
     
       // Date validation (MM-DD-YYYY format)
       const isValidDate = /^\d{2}-\d{2}-\d{4}$/.test(displayDate);
     
       return (
         <Box
           sx={{
             display: 'flex',
             alignItems: 'center',
             position: 'relative',
             height: '100%',
             width: '100%'
           }}
         >
          <Tooltip
            title={
              isValidDate
                ? ''
                : 'Invalid date format. Please use format MM-DD-YYYY.'
            }
            placement="bottom"
            arrow
          >
           <Box sx={{
             backgroundColor: isValidDate ? 'transparent' : 'rgba(227, 0, 0, 0.1)',
             padding: isValidDate ? '0px' : '8px',
             borderRadius: '4px',
             width: '100%'
           }}>
             
               <Typography variant="body2">{displayDate}</Typography>
           </Box>
        </Tooltip>  
         </Box>
       );
     },      
   };
 } else if (key === 'Purchase Date') {
   return {
     field: key,
     headerName: key,
     flex: 1,
     minWidth: 100,
     editable: true,
     renderEditCell: (params) => (
       <TextField
         value={params.value || ''}
         onChange={(event) => {
           const value = event.target.value;

           // Update the grid's internal state
           params.api.setEditCellValue({ id: params.id, field: key, value }, event);

           setMyData((prevData) =>
             prevData.map((row) =>
               row.id === params.id ? { ...row, [key]: value } : row
             )
           );

           // Update component's rows state
           setRows((prevRows) =>
             prevRows.map((row) =>
               row.id === params.id ? { ...row, [key]: value } : row
             )
           );
         }}
         placeholder="MM-DD-YYYY"
         variant="outlined"
         error={!/^\d{2}-\d{2}-\d{4}$/.test(params.value)}
         helperText={!/^\d{2}-\d{2}-\d{4}$/.test(params.value) ? 'Invalid date format' : ''}
       />
     ),
     renderCell: (params) => {
       const excelDate = params.value;
       const isNumber = !isNaN(excelDate);
       const jsDate = isNumber ? excelDateToJSDate(excelDate) : parseDateAsUTC(excelDate); // Convert if it's a number
       const displayDate = formatDate(jsDate); // Format to MM-DD-YYYY
     
       // Date validation (MM-DD-YYYY format)
       const isValidDate = /^\d{2}-\d{2}-\d{4}$/.test(displayDate);
     
       return (
         <Box
           sx={{
             display: 'flex',
             alignItems: 'center',
             position: 'relative',
             height: '100%',
             width: '100%'
           }}
         >
          <Tooltip
            title={
              isValidDate
                ? ''
                : 'Invalid date format. Please use format MM-DD-YYYY.'
            }
            placement="bottom"
            arrow
          >
           <Box sx={{
             backgroundColor: isValidDate ? 'transparent' : 'rgba(227, 0, 0, 0.1)',
             padding: isValidDate ? '0px' : '8px',
             borderRadius: '4px',
             width: '100%'
           }}>
             
               <Typography variant="body2">{displayDate}</Typography>
           </Box>
        </Tooltip>
         </Box>
       );
     },      
   };
 } else if (key === 'Warranty Expiration Date') {
   return {
     field: key,
     headerName: key,
     flex: 1,
     minWidth: 100,
     editable: true,
     renderEditCell: (params) => (
       <TextField
         value={params.value || ''}
         onChange={(event) => {
           const value = event.target.value;

           // Update the grid's internal state
           params.api.setEditCellValue({ id: params.id, field: key, value }, event);

           setMyData((prevData) =>
             prevData.map((row) =>
               row.id === params.id ? { ...row, [key]: value } : row
             )
           );

           // Update component's rows state
           setRows((prevRows) =>
             prevRows.map((row) =>
               row.id === params.id ? { ...row, [key]: value } : row
             )
           );
         }}
         placeholder="MM-DD-YYYY"
         variant="outlined"
         error={!/^\d{2}-\d{2}-\d{4}$/.test(params.value)}
         helperText={!/^\d{2}-\d{2}-\d{4}$/.test(params.value) ? 'Invalid date format' : ''}
       />
     ),
     renderCell: (params) => {
       const excelDate = params.value;
       const isNumber = !isNaN(excelDate);
       const jsDate = isNumber ? excelDateToJSDate(excelDate) : parseDateAsUTC(excelDate); // Convert if it's a number
       const displayDate = formatDate(jsDate); // Format to MM-DD-YYYY
     
       // Date validation (MM-DD-YYYY format)
       const isValidDate = /^\d{2}-\d{2}-\d{4}$/.test(displayDate);
     
       return (
         <Box
           sx={{
             display: 'flex',
             alignItems: 'center',
             position: 'relative',
             height: '100%',
             width: '100%'
           }}
         >
          <Tooltip
            title={
              isValidDate
                ? ''
                : 'Invalid date format. Please use format MM-DD-YYYY.'
            }
            placement="bottom"
            arrow
          >
           <Box sx={{
             backgroundColor: isValidDate ? 'transparent' : 'rgba(227, 0, 0, 0.1)',
             padding: isValidDate ? '0px' : '8px',
             borderRadius: '4px',
             width: '100%'
           }}>
             
               <Typography variant="body2">{displayDate}</Typography>
           </Box>
        </Tooltip>
         </Box>
       );
     },      
   };
 } else if (key === 'Asset Name') {
  return {
    field: key,
    headerName: key,
    flex: 1,
    minWidth: 150,
    editable: true,
    renderEditCell: params => {
      const value = params.value || ''
      const isValid = Boolean(value)
      return (
        <TextField
          value={value}
          variant="outlined"
          fullWidth
          error={!isValid}
          helperText={!isValid ? `Invalid ${key}. This asset will be skipped` : ''}
          onChange={e => {
            const newVal = e.target.value
            // update grid
            params.api.setEditCellValue(
              { id: params.id, field: key, value: newVal },
              e
            )
            // update your states
            setMyData(prev =>
              prev.map(r =>
                r.id === params.id ? { ...r, [key]: newVal } : r
              )
            )
            setRows(prev =>
              prev.map(r =>
                r.id === params.id ? { ...r, [key]: newVal } : r
              )
            )
          }}
        />
      )
    },
    renderCell: params => {
      const val = params.value
      
      const isValid = Boolean(val)
      console.log(isValid)
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            width: '100%',
          }}
        >
         <Tooltip
            title={
              isValid ? '' : `Invalid ${key}. This asset will be skipped`
            }
            placement="bottom"
            arrow
          >
          <Box
            sx={{
              backgroundColor: isValid
                ? 'transparent'
                : 'rgba(227, 0, 0, 0.1)',
              padding: isValid ? 0 : '8px',
              borderRadius: '4px',
              width: '100%',
            }}
          >

              <Typography variant="body2">
                {val || ''}
              </Typography>
          </Box>
        </Tooltip>
        </Box>
      )
    },
  }
} else {
   return {
     field: key,
     headerName: key,
     flex: 1,
     minWidth: 100,
     editable: true,
     renderEditCell: (params) => (
       <TextField
         value={params.value || ''}
         onChange={(event) => {
           const value = event.target.value;
           
           // Update the grid's internal state
           params.api.setEditCellValue({ id: params.id, field: key, value }, event);

           setMyData((prevData) =>
             prevData.map((row) =>
               row.id === params.id ? { ...row, [key]: value } : row
             )
           );

           // Update component's rows state
           setRows((prevRows) =>
             prevRows.map((row) =>
               row.id === params.id ? { ...row, [key]: value } : row
             )
           );
         }}
         variant="outlined"
       />
     ),
     renderCell: (params) => (
       <Box
         sx={{
           display: 'flex',
             alignItems: 'center',
             position: 'relative',
             height: '100%',
             width: '100%'
         }}
       >
         <Box sx={{
             backgroundColor: 'transparent',
             padding: '0px',
             borderRadius: '4px',
             width: '100%'
           }}>
           <Typography variant="body2" sx={{ color: 'text.primary' }}>
             {params.value}
           </Typography>
           </Box>
       </Box>
     ),
   };
 }
});

useEffect(() => {
  if (myData.length) {
    setColumns(buildColumns())
  }
}, [myData, assets, buildColumns])

const handleNext = async (event) => {

  setMessage('');
// console.log(event.target?.innerText);
// Move to the next step
if (event.target?.innerText === 'Complete Import') {
  // 1) Ensure we still have a session
  const token = localStorage.getItem('jwtToken');
  if (!token) {
    setLoading(false);
    navigate('/login', { state: { message: 'Session expired, please login again.' } });
    return;
  }

  // 2) Helper to clean numeric values (remove $, commas, spaces, quotes, etc.)
  const cleanNumericValue = (val) => {
    if (!val) return 0;
    // Convert to string and remove all non-numeric characters except decimal point and minus sign
    const cleaned = val.toString().replace(/[^0-9.-]/g, '');
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
  };

  // 3) Helper to normalize any date input into YYYY-MM-DD
  const normalizeDate = (val) => {
    let d;
    if (typeof val === 'number') {
      // Excel serial
      d = excelDateToJSDate(val);
    } else if (typeof val === 'string') {
      // Try MM-DD-YYYY as UTC first
      const utc = parseDateAsUTC(val);
      if (!isNaN(utc.getTime())) {
        d = utc;
      } else {
        // Fallback to Date.parse
        d = new Date(val);
      }
    } else {
      // Date object or other
      d = new Date(val);
    }

    if (!d || isNaN(d.getTime())) return '1900-01-01';
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  // 4) Filter out any completely invalid rows (only Asset Name required)
  const validRows = myData.filter(row =>
    row['Asset Name']?.toString().trim() 
    // &&
    // row['Serial/VIN Number']?.toString().trim()
  );

  // 5) Build your API DTOs
  const otherAssetType = assets.find(a => a.value === 'Other');
  
  const apiPayload = validRows.map(row => {
    // Determine numeric assetTypeID, default to "Other" if not set
    const rawType = row['Asset Type'];
    let assetTypeID;
    
    if (rawType && rawType !== '') {
      assetTypeID = !isNaN(rawType)
        ? Number(rawType)
        : (assets.find(a => a.value === rawType)?.key ?? (otherAssetType?.key || 0));
    } else {
      // If Asset Type is not set, use "Other"
      assetTypeID = otherAssetType?.key || 0;
    }

    // Determine operationalCostsUnit, default to "Day" if not set or invalid
    const rawUnit = row['Operational Cost Unit'];
    const operationalCostsUnit = operationalCostUnitOptions.includes(rawUnit) ? rawUnit : 'Day';

    return {
      assetID: 0,
      assetName: row['Asset Name'].toString().trim(),
      assetTypeID,
      serialVINNumber: row['Serial/VIN Number'] ? row['Serial/VIN Number'].toString().trim() : null || null,
      licensePlate: row['License Plate'] ? row['License Plate'].toString().trim() : null || null,
      operationalCostsAmount: cleanNumericValue(row['Operational Cost']),
      operationalCostsUnit: operationalCostsUnit ? operationalCostsUnit.toLowerCase() : 'day',
      description: row['Asset Description'] || null,
      hourlyRate: cleanNumericValue(row['Hourly Rate']),
      designatedOperator: row['Designated Operator'] || null,
      status: normalizeStatus(row['Status']),
      nextMaintenanceDate: normalizeDate(row['Next Maintenance Date']),
      lastMaintenanceDate: normalizeDate(row['Last Maintenance Date']),
      purchaseDate: normalizeDate(row['Purchase Date']),
      purchasePrice: cleanNumericValue(row['Purchase Price']),
      warrantyExpiration: normalizeDate(row['Warranty Expiration Date']),
      isActive: true
    };
  });

  console.log(apiPayload)
  // 6) Persist one‐by‐one, collecting any failures
  setLoading(true);
  const failures = [];
  for (let i = 0; i < apiPayload.length; i++) {
    const dto = apiPayload[i];
    try {
      const res = await SaveAsset(dto);
      console.log(res)
      if (res?.isError) {
        failures.push({
          id: i,
          assetName: dto.assetName,
          failureReason: res.message || 'Unknown error'
        });
      } else if (res?.errors) {
        // flatten validation errors
        const msg = Object.entries(res.errors)
          .flatMap(([field, arr]) => arr.map(m => `${field}: ${m}`))
          .join('; ');
        failures.push({ id: i, assetName: dto.assetName, failureReason: msg });
      }
    } catch (err) {
      // network / unexpected failure
      failures.push({
        id: i,
        assetName: dto.assetName,
        failureReason: err.message
      });
    }
  }

  // 7) Show results in your dialogs
  if (failures.length) {
    setErrorMessages(failures);
    setOpenErrorListDlg(true);
  } else {
    setSuccessMessages('All assets saved successfully.');
    setOpenSuccessListDlg(true);
  }
  setLoading(false);
} else {
  
  const allMappedOrIgnored = columnNamesFromFile.every(
      (columnName) => mappingState[columnName]?.confirmed || mappingState[columnName]?.ignored
    );
    
    if (!allMappedOrIgnored) {
      setMessage('Please confirm mapping or ignore all columns before continuing.');
      return;
    }

    const requiredFields = ["Asset Name"];
    const isMappingComplete = requiredFields.every(field =>
      Object.values(mappingState).some(mapping => mapping.mappedValue === field && mapping.confirmed)
    );

    if (!isMappingComplete) {
      setMessage('Please map all the required fields: Asset Name before proceeding.');
      return;
    }

  // Check if Asset Type column was mapped
  const assetTypeCol = Object
    .entries(mappingState)
    .find(([col, info]) => info.confirmed && info.mappedValue === 'Asset Type')?.[0]

  let lookup = assets
  const otherType = lookup.find(a => a.value === 'Other')

  // If Asset Type column exists in mapping
  if (assetTypeCol) {
    const rawTypes = Array.from(new Set(
      fileData.map(r => r[assetTypeCol]).filter(v => v && v !== 'None')
    ))

    const existingNames = lookup.map(a => a.value)
    const missingNames = rawTypes.filter(name => !existingNames.includes(name))

    // If any are missing, save + re-fetch
    if (missingNames.length) {
      setLoading(true)
      for (const name of missingNames) {
        await SaveTypeCatalog({
          typeCatalogID: 0,
          entityType: 'Asset',
          typeName: name,
          name: name
        })
      }
      const refreshed = await GetTypeCatalog('Asset')
      if (!refreshed.isError) {
        lookup = refreshed.result.map(a => ({ key: a.typeCatalogID, value: a.typeName }))
        setAssets(lookup)
      }
      setLoading(false)
    }
  }

  // Build mappedData with Asset Type and Operational Cost Unit handling
  const mappedData = fileData
    .map((row, i) => {
      const out = { id: i.toString() }
      
      // Track if Asset Type was set from file
      let assetTypeSet = false
      
      for (const col in row) {
        if (mappingState[col]?.confirmed) {
          const field = mappingState[col].mappedValue
          if (field === 'Asset Type') {
            const text = row[col]
            // If value exists and is not empty
            if (text && text.toString().trim() !== '') {
              const match = lookup.find(a => a.value === text)
              out['Asset Type'] = match ? match.key : otherType?.key || ''
              assetTypeSet = true
            }
          } else if (field === 'Operational Cost Unit') {
            const text = row[col]
            // Add to grid data (even if empty, so user can edit it)
            out['Operational Cost Unit'] = text || ''
          } else {
            out[field] = row[col]
          }
        }
      }
      
      // If Asset Type wasn't set from file (column missing or value empty), use "Other"
      if (!assetTypeSet && otherType) {
        out['Asset Type'] = otherType.key
      }
      
      // Note: We don't add Operational Cost Unit here if not in file
      // It will default to "Day" in the API payload construction
      
      return out
    })
    .filter(r => Object.keys(r).length > 1)

  setMyData(mappedData)
  setRows(mappedData)           // or however you slice into pages
  setPaginationModel({ page: 0, pageSize: mappedData.length })
  setActiveStep(s => s + 1)

 }

};

const [alignment, setAlignment] = useState('all')

// build your three buckets
const isValidDate = value => {
  if (value == null || value === '') return false;

  // 1) Excel serial date (a pure number)
  if (typeof value === 'number') {
    const jsDate = excelDateToJSDate(value);
    return !isNaN(jsDate.getTime());
  }

  // 2) “MM/DD/YYYY” or “MM-DD-YYYY”
  //    Normalize to slashes, then split
  const parts = value.replace(/-/g, '/').split('/');
  if (parts.length === 3) {
    const [mm, dd, yyyy] = parts.map(x => parseInt(x, 10));
    if (
      String(mm).length > 0 &&
      String(dd).length > 0 &&
      String(yyyy).length === 4
    ) {
      const d = new Date(yyyy, mm - 1, dd);
      return (
        d.getFullYear() === yyyy &&
        d.getMonth() === mm - 1 &&
        d.getDate() === dd
      );
    }
  }

  // 3) Fallback: let Date.parse handle anything else
  return !isNaN(Date.parse(value));
};

const allRows = rows

function isRowCompletelyValid(row) {
  // if (!validateEmail(row.Email))                           return false
  // if (!isValidPhone(String(row.Phone||'')))                return false
  // if (!row['First Name']?.trim() || !row['Last Name']?.trim()) return false
  // if (!rolesOptions.includes(row.Role))                    return false
  // if (!employeeStatusOptions.includes(row['Employment Status'])) return false
  // if (!statusOptions.includes(row.Status))                 return false
  // if (!isValidDate(row['Date of Birth']))                  return false
  // if (!isValidDate(row['Hire Date']))                       return false
  // if (!isValidDate(row['Probation End Date']))             return false
  // if (!isValidDate(row['Driving license expiry']))         return false
  // if (!employeeWagesType.includes(row['Employee Wages Type'])) return false
  // etc… check any other dropdown / boolean / numeric cell you have
  return true
}

const validRows   = allRows.filter(isRowCompletelyValid)
const invalidRows = allRows.filter(r => !isRowCompletelyValid(r))


// const validRows = allRows.filter(row => {
//   const emailOk = validateEmail(row.Email)
//   const phoneOk = isValidPhone(String(row.Phone || ''))
//   const namesOk = Boolean(row['First Name']?.trim() && row['Last Name']?.trim())
//   return emailOk && phoneOk && namesOk
// })

// const invalidRows = allRows.filter(r => !validRows.includes(r))

// choose which to show
const displayRows = alignment === 'all'
  ? allRows
  : alignment === 'valid'
    ? validRows
    : invalidRows

const handleAlignment = (event, newAlignment) => {
  if (newAlignment !== null) {
    setAlignment(newAlignment)
  }
}

  const getStepContent = step => {
    switch (step) {
      case 0:
        return (
          <Fragment>
            <Grid container spacing={5} //sx={{ mt: 1 }}
            >
              {/* {displayBanner && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "inline-flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      backgroundColor: "rgb(35, 162, 109, 0.1)",
                      border: "1px solid",
                      borderColor: "success.main",
                      borderRadius: "12px",
                      py: 1,
                      px: 3,
                      gap: 1,
                    }}
                  >
                    <Icon icon="tabler:info-circle" />
                    <Typography
                      variant="h6"
                      fontWeight="500"
                      sx={{ ml: 2, mr: 3 }}
                    >
                      These fields are editable! -You can use your AI Assistant
                      to do this part later.
                    </Typography>
                    <Tooltip title="Remove">
                      <IconButton onClick={() => setDisplayBanner(false)}>
                        <Icon icon="tabler:x" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
              )} */}
              {showMandatoryAlert && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "inline-flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      backgroundColor: "rgba(255, 159, 67, 0.16)",
                      border: "1px solid",
                      borderColor: "warning.main",
                      borderRadius: "12px",
                      py: 1,
                      px: 3,
                      gap: 2,
                    }}
                  >
                    <Icon icon="tabler:alert-triangle" />
                    <Typography variant="h6" fontWeight="500">
                      Please ensure {unmappedFields.length === 2 
                        ? `the ${unmappedFields.join(' and ')} fields are` 
                        : `the ${unmappedFields[0]} field is`} mapped as {unmappedFields.length === 1 ? "it's" : "they are"} required to proceed with the next steps.
                    </Typography>
                    <IconButton onClick={handleAlertDismiss} color="warning">
                      <Icon icon="tabler:x" />
                    </IconButton>
                  </Box>
                </Grid>
              )}
              <Grid item xs={12} sm={4}>
                <Typography
                  variant="h5"
                  color="text.secondary"
                  fontWeight="550"
                  gutterBottom
                >
                  File Source Fields
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography
                  variant="h5"
                  color="text.secondary"
                  fontWeight="550"
                  gutterBottom
                >
                  Cleri Fields
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                {/* Empty grid to align the third column (status buttons) */}
              </Grid>

              {/* Dropdowns Row */}
              {columnNamesFromFile.map((columnName, colIndex) => (
                <Fragment key={colIndex}>
                  <Grid item xs={12} sm={4}>
                    <CustomTextField
                      fullWidth
                      label=""
                      value={columnName}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <CustomTextField
                      fullWidth
                      select
                      // defaultValue={
                      //   fieldsToRender.includes(columnName)
                      //     ? columnName
                      //     : fieldsToRender.some(field => field.replace(/\s/g, '').toLowerCase() === columnName.replace(/\s/g, '').toLowerCase())
                      //       ? fieldsToRender.find(field => field.replace(/\s/g, '').toLowerCase() === columnName.replace(/\s/g, '').toLowerCase())
                      //       : 'select'
                      // }
                      value={
                        selectedValues[columnName] ||
                        getDefaultValues()[columnName]
                      }
                      label=""
                      onChange={handleSelectChange(columnName)}
                      error={!!errorState[columnName]} // Apply error if duplicate is detected
                      helperText={errorState[columnName]} // Show helper text if error exists
                      disabled={
                        mappingState[columnName]?.confirmed ||
                        mappingState[columnName]?.ignored
                      }
                    >
                      <ListSubheader>Mandatory Fields</ListSubheader>
                      <MenuItem value="select" disabled>
                        Select
                      </MenuItem>
                      {predefinedFields.map((field, index) => (
                        <MenuItem key={index} value={field}>
                          {field}
                        </MenuItem>
                      ))}
                      <ListSubheader>Optional Fields</ListSubheader>
                      {additionalFields.map((field, index) => (
                        <MenuItem key={index} value={field}>
                          {field}
                        </MenuItem>
                      ))}
                    </CustomTextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    {mappingState[columnName]?.confirmed ? (
                      <Box sx={{ display: "flex" }}>
                        <Icon>
                          <CheckCircleIcon
                            sx={{
                              color: "#1FC16B",
                              height: "17px",
                              width: "17px",
                              my: "auto",
                              mr: 2,
                            }}
                          />
                        </Icon>
                        <Typography
                          sx={{
                            fontWeight: "bold",
                            color: "success",
                            my: "auto",
                            mr: 4,
                          }}
                        >
                          Mapping Confirmed
                        </Typography>
                        <Button
                          variant="contained"
                          color="main"
                          onClick={() => handleEditClick(columnName)}
                        >
                          Edit
                        </Button>
                      </Box>
                    ) : mappingState[columnName]?.ignored ? (
                      <Box sx={{ display: "flex" }}>
                        <Typography
                          sx={{ fontWeight: "bold", my: "auto", mr: 4 }}
                        >
                          Ignored
                        </Typography>
                        <Button
                          variant="contained"
                          color="main"
                          onClick={() => handleEditClick(columnName)}
                        >
                          Edit
                        </Button>
                      </Box>
                    ) : (
                      <>
                        <Button
                          variant="contained"
                          sx={{ mr: 4, mb: 2 }}
                          color="main"
                          onClick={() => handleConfirmClick(columnName)}
                          disabled={isConfirmDisabled(columnName)} // Disable based on conditions
                        >
                          Confirm mapping
                        </Button>
                        <Button
                          variant="outlined"
                          color="primary"
                          sx={{ mb: 2 }}
                          onClick={() => handleIgnoreClick(columnName)}
                        >
                          Ignore this column
                        </Button>
                      </>
                    )}
                  </Grid>
                </Fragment>
              ))}
            </Grid>
          </Fragment>
        );
      case 1:
        return (
          <Fragment>          
          <Grid item xs={12} sm={12}>
            <ToggleButtonGroup
              exclusive
              value={alignment}
              onChange={handleAlignment}
              aria-label='text alignment'
              sx={{
                backgroundColor: '#F5F7FA',
                borderRadius: 2,
                p: 2,
                '& .MuiToggleButtonGroup-grouped': {
                  margin: '0',
                  border: 'none',
                  borderRadius: 1,
                  '&:not(:first-of-type)': {
                    borderRadius: '4px !important'
                  }
                }
              }}
            >
              <ToggleButton 
                value='all' 
                aria-label='left aligned'
                sx={{
                  px: 6,
                  '&.Mui-selected': {
                    backgroundColor: '#fff',
                    color: 'white !important',
                    '&:hover': {
                      backgroundColor: '#fff !important',
                    }
                  },
                  '&:not(.Mui-selected)': {
                    color: '#6B7280',
                  }
                }}
              >
                <Typography variant='body1' 
                  sx={{ 
                    textTransform: 'lowercase',
                    '&::first-letter': {
                      textTransform: 'uppercase'
                    }
                  }}
                >
                  All {allRows.length}
                </Typography>
              </ToggleButton>
              <ToggleButton 
                value='valid' 
                aria-label='left aligned'
                sx={{
                  px: 6,
                  '&.Mui-selected': {
                    backgroundColor: '#fff',
                    color: 'white !important',
                    '&:hover': {
                      backgroundColor: '#fff !important',
                    }
                  },
                  '&:not(.Mui-selected)': {
                    color: '#6B7280',
                  }
                }}
              >
                <Typography variant='body1' 
                  sx={{ 
                    textTransform: 'lowercase',
                    '&::first-letter': {
                      textTransform: 'uppercase'
                    }
                  }}
                >
                  Valid {validRows.length}
                </Typography>
              </ToggleButton>
              <ToggleButton 
                value='invalid' 
                aria-label='left aligned'
                sx={{
                  px: 6,
                  '&.Mui-selected': {
                    backgroundColor: '#fff',
                    color: 'white !important',
                    '&:hover': {
                      backgroundColor: '#fff !important',
                    }
                  },
                  '&:not(.Mui-selected)': {
                    color: '#6B7280',
                  }
                }}
              >
                <Typography variant='body1' 
                  sx={{ 
                    textTransform: 'lowercase',
                    '&::first-letter': {
                      textTransform: 'uppercase'
                    },
                    color:'#D73838'
                  }}
                >
                  Invalid {invalidRows.length}
                </Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid item xs={12} sm={12}>
            <DataGridPro
              autoHeight
              pagination
              rows={displayRows}
              rowCount={displayRows.length}
              columns={columns}
              getRowId={(row) => row.id}              
              sortingMode="server"
              paginationMode="server"
              pageSizeOptions={[rows.length, 10, 25, 50]}
              paginationModel={paginationModel}
              onSortModelChange={handleSortModel}
              onPaginationModelChange={setPaginationModel}
              editMode="cell"
              // onCellEditCommit={handleEditCellChangeCommitted}
              slots={{ toolbar: ServerSideToolbar }}
              slotProps={{
                baseButton: {
                  size: 'medium',
                  variant: 'tonal',
                },
                toolbar: {
                  value: searchValue,
                  clearSearch: () => handleSearch(''),
                  onChange: (event) => handleSearch(event.target.value),
                  valueExists: searchValue !== '',
                },
              }}
              sx={{
                '& .MuiDataGrid-columnHeaderTitle': {
                  textTransform: 'none', // This will disable the capitalization
                }
              }}
            />
          </Grid>
        </Fragment>
        )
      default:
        return 'Unknown Step'
    }
  }

  const renderContent = () => {
      return (
        <>
        {message && (
          <Box mb={2} textAlign="center">
            <Typography variant="h6" color={message.includes('successfully') ? 'green' : 'red'}>
              {message.split('\n').map((line, index) => (
                <div key={index}>{line}</div>
              ))}
            </Typography>
          </Box>
        )}
        <form onSubmit={e => e.preventDefault()} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        {getStepContent(activeStep)}
        <Grid item xs={12} sx={{ position: 'fixed', bottom: 0, right: 0, width: '100vw', display: 'flex', justifyContent: 'flex-end', py: 4, pr: 10, backgroundColor: 'white', boxShadow: '0 -2px 5px rgba(0,0,0,0.1)' }}>        
          <Button variant='tonal' color='secondary'  onClick={handleBack} sx={{ mr: 4 }}
          // disabled={activeStep === 0}
          >
            Back
          </Button>              
          <Button variant='contained' color='main' onClick={handleNext}  disabled={loading} >
          {activeStep === steps.length - 1 ? 'Complete Import' : 'Continue'}
          </Button>
        </Grid>
    </form>
    </>
      )
  }

  return (
    <BlankLayout>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '92vh' }}>
          <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, }}>
                  <Box>
                    <img src={XLSX_Placeholder} />
                  </Box>
                  <Box>
                    <Typography variant='h6'>Import data to Assets</Typography>
                    <Typography variant='body1' color='text.secondary'>Assets data.csv</Typography>
                  </Box>
                </Box>
              }
              action={
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100% !important', py: 3, }}>
                  <StepperWrapper>
                    <Stepper
                      activeStep={activeStep}
                      connector={
                        !smallScreen ? <Icon icon={direction === 'ltr' ? 'tabler:chevron-right' : 'tabler:chevron-left'} /> : null
                      }
                    >
                      <Step>
                        <StepLabel StepIconComponent={StepperCustomDot}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: 'success.main' }}>
                            <Icon icon='tabler:circle-check-filled' />
                            <Typography color='success.main'>Upload</Typography>
                          </Box>
                        </StepLabel>
                      </Step>
                      {steps.map((step, index) => (
                        <Step key={index}>
                          <StepLabel StepIconComponent={StepperCustomDot}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: index >= activeStep ? 'text.primary' : 'success.main' }}>
                              {index < activeStep && (<Icon icon='tabler:circle-check-filled' />)}
                              <Typography color={index >= activeStep ? 'text.primary' : 'success.main'}>{step.title}</Typography>
                            </Box>
                          </StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  </StepperWrapper>
                </Box>
              }
            />
            <Divider />
            <CardContent sx={{ flexGrow: 1, overflowY: 'auto' }}>
              {renderContent()}
            </CardContent>
          </Card>
        </Box>

        {/* Error List Dialog */}      
        <Dialog
          fullWidth
          maxWidth="md"
          open={openErrorListDlg}
          onClose={handleCloseErrorListDlg}
          aria-labelledby="form-dialog-title"
          sx={{
            "& .MuiDialog-paper": {
              maxWidth: '650px', // Custom maxWidth between sm and md
              width: '100%', // Ensures it’s responsive within the set max width
            },
          }}
        >
          <DialogTitle>
            <Typography variant="h4" fontWeight='bold'>Save Unsuccessful</Typography>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={5}>
              <Grid item xs={12}>
                <Typography variant="button">The following assets will be skipped due to the reasons listed below.</Typography>
              </Grid>
              <Grid item xs={12}>
                <DataGridPro
                  autoHeight              
                  rows={errorMessages}
                  getRowId={(row) => row.id}
                  columns={errorColumns}           
                  sx={{
                    "& .MuiDataGrid-columnHeaderTitle": {
                      textTransform: "none",
                      fontWeight: 'bold'
                    },
                    maxWidth: '600px'
                  }}
                />
              </Grid>
            </Grid>     
          </DialogContent>
          <DialogActions
            sx={{ display: "flex", justifyContent: "flex-end" }}
            className="dialog-actions-dense"
          >
            <Button variant="contained" color="main" onClick={handleCloseErrorListDlg}>
              OK
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success List Dialog */}      
        <Dialog
          fullWidth={true}
          maxWidth="xs"
          open={openSuccessListDlg}
          onClose={handleCloseSuccessListDlg}
          aria-labelledby="form-dialog-title"
        >
          <DialogTitle>
            <Typography variant="h4" fontWeight='bold'>Assets Import Success</Typography>
          </DialogTitle>
          <DialogContent>
            <Fragment>          
              {successMessages}
            </Fragment>
          </DialogContent>
          <DialogActions
            sx={{ display: "flex", justifyContent: "flex-end" }}
            className="dialog-actions-dense"
          >
            <Button variant="contained" color="main" onClick={handleCloseSuccessListDlg}>
              OK
            </Button>
          </DialogActions>
        </Dialog>
        <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </BlankLayout>
  )
}

export default StepAssetUploadFile